import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { ShippingStatus } from "@/types/shipping";

interface EmployeeOption {
  id: string;
  name: string;
  email: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  isAdmin: boolean;
  currentEmployeeId: string | null;
}

interface OrderForm {
  orderNumber: string;
  customer: string;
  customerType: string;
  product: string;
  quantity: number;
  unit: string;
  logistics: string;
  trackingNumber: string;
  expectedArrival: string;
  deliveryDeadline: string;
  amount: number;
  currency: string;
  status: ShippingStatus;
  employeeId: string;
}

const emptyForm: OrderForm = {
  orderNumber: "",
  customer: "",
  customerType: "",
  product: "",
  quantity: 1,
  unit: "瓶",
  logistics: "",
  trackingNumber: "",
  expectedArrival: "",
  deliveryDeadline: "",
  amount: 0,
  currency: "NT$",
  status: "海運中",
  employeeId: "",
};

export default function CreateOrderModal({ open, onClose, onCreated, isAdmin, currentEmployeeId }: Props) {
  const [form, setForm] = useState<OrderForm>({ ...emptyForm, employeeId: currentEmployeeId || "" });
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    supabase
      .from("employees")
      .select("id, name, email")
      .order("name")
      .then(({ data }) => {
        if (data) setEmployees(data as EmployeeOption[]);
      });
    setForm({ ...emptyForm, employeeId: currentEmployeeId || "" });
    setError("");
  }, [open, currentEmployeeId]);

  if (!open) return null;

  const handleChange = (field: keyof OrderForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): string | null => {
    if (!form.orderNumber.trim()) return "請輸入訂單編號";
    if (!form.customer.trim()) return "請輸入客戶名稱";
    if (!form.product.trim()) return "請輸入商品名稱";
    if (!form.expectedArrival) return "請選擇預計到港時間";
    if (!form.deliveryDeadline) return "請選擇訂單交付期限";
    if (!form.employeeId) return "請選擇指派員工";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError("");

    const basePayload = {
      order_number: form.orderNumber.trim(),
      customer: form.customer.trim(),
      customer_type: form.customerType.trim(),
      product: form.product.trim(),
      quantity: form.quantity,
      unit: form.unit,
      logistics: form.logistics.trim(),
      tracking_number: form.trackingNumber.trim(),
      expected_arrival: form.expectedArrival,
      delivery_deadline: form.deliveryDeadline,
      amount: form.amount,
      currency: form.currency,
      status: form.status,
    };

    try {
      if (form.employeeId === "__all__") {
        // Create one order per employee
        const payloads = employees.map((emp) => ({
          ...basePayload,
          employee_id: emp.id,
        }));
        const { error: insertErr } = await supabase.from("shipping_orders").insert(payloads);
        if (insertErr) throw new Error(insertErr.message);
      } else {
        const { error: insertErr } = await supabase.from("shipping_orders").insert({
          ...basePayload,
          employee_id: form.employeeId,
        });
        if (insertErr) throw new Error(insertErr.message);
      }

      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "建立失敗");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 bg-background-50";
  const labelClass = "text-xs font-medium text-foreground-600 mb-1 block";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-background-50 rounded-xl border border-background-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div>
            <h3 className="text-lg font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
              新增貨運訂單
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">手動建立一筆新的貨運追蹤訂單</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          {/* Row 1: Order Number + Customer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>訂單編號 <span className="text-accent-500">*</span></label>
              <input type="text" value={form.orderNumber} onChange={(e) => handleChange("orderNumber", e.target.value)} placeholder="SH-20260703-001" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>客戶名稱 <span className="text-accent-500">*</span></label>
              <input type="text" value={form.customer} onChange={(e) => handleChange("customer", e.target.value)} placeholder="客戶公司/姓名" className={inputClass} />
            </div>
          </div>

          {/* Row 2: Customer Type + Product */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>客戶類型</label>
              <input type="text" value={form.customerType} onChange={(e) => handleChange("customerType", e.target.value)} placeholder="例如：經銷商 / 直客" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>商品名稱 <span className="text-accent-500">*</span></label>
              <input type="text" value={form.product} onChange={(e) => handleChange("product", e.target.value)} placeholder="商品名稱" className={inputClass} />
            </div>
          </div>

          {/* Row 3: Quantity + Unit */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>數量</label>
              <input type="number" min={1} value={form.quantity} onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>單位</label>
              <select value={form.unit} onChange={(e) => handleChange("unit", e.target.value)} className={inputClass}>
                <option value="瓶">瓶</option>
                <option value="箱">箱</option>
                <option value="件">件</option>
                <option value="組">組</option>
                <option value="公斤">公斤</option>
                <option value="公噸">公噸</option>
              </select>
            </div>
          </div>

          {/* Row 4: Logistics + Tracking Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>物流商</label>
              <input type="text" value={form.logistics} onChange={(e) => handleChange("logistics", e.target.value)} placeholder="例如：Maersk / DHL" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>追蹤號碼</label>
              <input type="text" value={form.trackingNumber} onChange={(e) => handleChange("trackingNumber", e.target.value)} placeholder="追蹤單號" className={inputClass} />
            </div>
          </div>

          {/* Row 5: Expected Arrival + Delivery Deadline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>預計到港時間 <span className="text-accent-500">*</span></label>
              <input type="date" value={form.expectedArrival} onChange={(e) => handleChange("expectedArrival", e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>訂單交付期限 <span className="text-accent-500">*</span></label>
              <input type="date" value={form.deliveryDeadline} onChange={(e) => handleChange("deliveryDeadline", e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Row 6: Amount */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className={labelClass}>金額（NT$）</label>
              <input type="number" min={0} step={0.01} value={form.amount} onChange={(e) => handleChange("amount", parseFloat(e.target.value) || 0)} className={inputClass} />
            </div>
          </div>

          {/* Row 7: Status + Employee */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>初始狀態</label>
              <select value={form.status} onChange={(e) => handleChange("status", e.target.value)} className={inputClass}>
                <option value="海運中">海運中</option>
                <option value="已到港">已到港</option>
                <option value="待付尾款">待付尾款</option>
                <option value="已轉帳務">已轉帳務</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>指派員工 <span className="text-accent-500">*</span></label>
              <select value={form.employeeId} onChange={(e) => handleChange("employeeId", e.target.value)} className={inputClass}>
                <option value="">請選擇員工</option>
                {isAdmin && <option value="__all__">── 全部員工（每人一筆）──</option>}
                {isAdmin && <option disabled>──────────────</option>}
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}（{emp.email}）
                  </option>
                ))}
              </select>
              {form.employeeId === "__all__" && (
                <p className="text-xs text-primary-600 mt-1 flex items-center gap-1">
                  <i className="ri-information-line" />
                  將為 {employees.length} 位員工各自建立一筆相同內容的訂單
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                建立中...
              </>
            ) : (
              <>
                <i className="ri-add-line" />
                建立訂單
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}